/**
 * ExecutionConfirmationDialog.tsx
 *
 * Modal that shows test summary and execution scope options.
 * Displays resource allocation preview and offers "Run Once" vs "Run Across Cluster" buttons.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, CreditCard, Server, Zap } from 'lucide-react';
import type { QuickTestType } from './QuickTestButton';

export interface ExecutionConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  testType: QuickTestType;
  testLabel: string;
  testDescription: string;
  selectionMode: 'individual' | 'cluster';
  clusterName?: string;
  locationNames: string[];
  eSimGroupName?: string;
  eSimProfileCount: number;
  /**
   * Set when step 2 selected ONE specific SIM instead of a group. Replaces the
   * group line so the confirmation names the SIM the run is actually bound to.
   */
  eSimProfileName?: string;
  /** Sub-line for a specific SIM (group · network · ICCID). */
  eSimProfileDetail?: string;
  onConfirm: (scope: 'once' | 'cluster') => void;
}

export function ExecutionConfirmationDialog({
  isOpen,
  onClose,
  testType,
  testLabel,
  testDescription,
  selectionMode,
  clusterName,
  locationNames,
  eSimGroupName,
  eSimProfileCount,
  eSimProfileName,
  eSimProfileDetail,
  onConfirm,
}: ExecutionConfirmationDialogProps) {
  const locationCount = locationNames.length;
  const isCluster = selectionMode === 'cluster';

  const handleConfirm = (scope: 'once' | 'cluster') => {
    onConfirm(scope);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Confirm Test Execution
          </DialogTitle>
          <DialogDescription>
            Review your selections and choose how to run the test
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {/* Test Information */}
          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Test</h3>
            <div className="p-3 bg-muted rounded-lg">
              <div className="font-medium text-foreground">{testLabel}</div>
              <div className="text-sm text-muted-foreground mt-1">{testDescription}</div>
            </div>
          </div>

          {/* Selected Resources */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground">Selected Resources</h3>

            {/* Cluster/Locations */}
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <MapPin className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">
                  {isCluster ? (
                    <>Cluster: {clusterName || 'Unknown'}</>
                  ) : (
                    <>Individual Locations</>
                  )}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {locationCount} location{locationCount !== 1 ? 's' : ''}
                  {locationNames.length > 0 && locationNames.length <= 5 && (
                    <div className="mt-1">
                      {locationNames.map((name, i) => (
                        <div key={i} className="text-xs">• {name}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* eSIM — one specific SIM, or a group the allocator picks from */}
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">
                  {eSimProfileName || eSimGroupName || 'eSIM'}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {eSimProfileName
                    ? eSimProfileDetail || 'This specific SIM'
                    : `${eSimProfileCount} profile${eSimProfileCount !== 1 ? 's' : ''} available`}
                </div>
              </div>
            </div>
          </div>

          {/* Estimated Allocation */}
          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Estimated Allocation</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Server className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium text-foreground">Modems</div>
                  <div className="text-xs text-muted-foreground">
                    1-{locationCount} (1 per location)
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium text-foreground">eSIM Profiles</div>
                  <div className="text-xs text-muted-foreground">
                    {/* A specific SIM is exactly one profile no matter how many
                        locations run — it can only live on one modem at a time. */}
                    {eSimProfileName ? '1 (this SIM)' : `1-${Math.min(locationCount, eSimProfileCount)}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleConfirm('once')}
            className="w-full sm:w-auto"
            data-testid="confirm-run-once"
          >
            Run Once
            <span className="text-xs text-muted-foreground ml-2">(1 test)</span>
          </Button>
          <Button
            onClick={() => handleConfirm('cluster')}
            className="w-full sm:w-auto"
          >
            Run Across {isCluster ? 'Cluster' : 'All'}
            <span className="text-xs opacity-75 ml-2">
              ({locationCount} test{locationCount !== 1 ? 's' : ''})
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
